/**
 * Quote Request Types
 * Types for the quote request feature
 */

export type TravelStatus = 'visiting_soon' | 'already_in_sri_lanka';

export type TravelStyle = 
  | 'relaxer'
  | 'adventure_hunter'
  | 'high_energy_explorer'
  | 'culture_seeker'
  | 'food_lover';

export type BudgetStyle =
  | 'smart_planner'
  | 'balance_seeker'
  | 'premium_picker';

export type CommunicationMethod = 'email' | 'whatsapp' | 'telegram';

export type ServiceType = 'full_trip' | 'driver_only';

export type VehicleType = 'car_suv' | 'mini_van' | 'large_van' | 'bus';

export interface DriverOnlyFormData {
  serviceType: 'driver_only';
  isLazy?: boolean;
  tripName?: string;
  travelStatus: TravelStatus;
  arrivalDate?: string; // Pre-populated from trip
  arrivalTime?: string;
  departureDate?: string; // Pre-populated from trip
  departureTime?: string;
  pickupTime?: string;
  dropoffTime?: string;
  travelers: {
    adults: number;
    children: number;
    infants: number;
  };
  vehicleType: VehicleType;
  specialNeeds: {
    hasSpecialNeeds: boolean;
    details?: string;
  };
  roughItinerary?: string; // For lazy quotes
  additionalNotes: string;
  languagePreference: string;
  termsAccepted: boolean; // Single term
  communicationMethod: CommunicationMethod;
}

export interface FullTripFormData {
  serviceType: 'full_trip';
  isLazy?: boolean;
  tripName?: string;
  travelStatus: TravelStatus;
  arrivalDate?: string;
  arrivalTime?: string;
  departureDate?: string;
  departureTime?: string;
  pickupTime?: string;
  dropoffTime?: string;
  travelers: {
    adults: number;
    children: number;
    infants: number;
  };
  placesToVisit?: string[]; // For lazy quotes
  otherPlaces?: string; // For lazy quotes
  activitiesToDo?: string[]; // For lazy quotes
  otherActivities?: string; // For lazy quotes
  travelStyle: TravelStyle;
  budgetStyle: BudgetStyle;
  languagePreference: string;
  additionalNotes: string;
  termsAccepted: [boolean, boolean, boolean]; // 3 terms
  communicationMethod: CommunicationMethod;
}

export type QuoteFormData = FullTripFormData | DriverOnlyFormData;

export interface QuoteRequest {
  requested: boolean;
  requestedAt: Date;
  serviceType: 'full_trip' | 'driver_only';
  travelStatus: TravelStatus;
  dates: {
    arrivalDate?: string;
    arrivalTime?: string;
    departureDate?: string;
    departureTime?: string;
    pickupTime?: string;
    dropoffTime?: string;
  };
  // For full_trip
  preferences?: {
    travelStyle: TravelStyle;
    budgetStyle: BudgetStyle;
    languagePreference: string;
    additionalNotes: string;
  };
  // For driver_only
  driverOnlyDetails?: {
    travelers: {
      adults: number;
      children: number;
      infants: number;
    };
    vehicleType: VehicleType;
    specialNeeds: {
      hasSpecialNeeds: boolean;
      details?: string;
    };
    languagePreference: string;
    additionalNotes: string;
  };
  communicationMethod: CommunicationMethod;
  status: 'pending' | 'processing' | 'completed';
}

// Style options with descriptions
export const TRAVEL_STYLES = [
  {
    id: 'relaxer' as TravelStyle,
    title: 'The Relaxer',
    shortDesc: 'Someone who travels to rest, recharge, and take it slow',
    fullDesc: 'Calm days, beaches, pools, spas, sunsets, minimal activity, no rush.'
  },
  {
    id: 'adventure_hunter' as TravelStyle,
    title: 'The Adventure Hunter',
    shortDesc: 'Travels for excitement and physical challenges',
    fullDesc: 'Hiking, waterfalls, surfing, rafting, climbing, exploring wild places.'
  },
  {
    id: 'high_energy_explorer' as TravelStyle,
    title: 'The High-Energy Explorer',
    shortDesc: 'Loves doing many things in one day and staying active',
    fullDesc: 'Packed itineraries, multiple stops, no "chill days," always on the move.'
  },
  {
    id: 'culture_seeker' as TravelStyle,
    title: 'The Culture & Local Life Seeker',
    shortDesc: 'Travels to learn, experience traditions, meet locals',
    fullDesc: 'Temples, cultural shows, food tours, heritage sites.'
  },
  {
    id: 'food_lover' as TravelStyle,
    title: 'The Food Lover',
    shortDesc: 'Travels mainly to try food and eat like a local',
    fullDesc: 'Street food, markets, restaurants, unique dishes.'
  }
];

export const BUDGET_STYLES = [
  {
    id: 'smart_planner' as BudgetStyle,
    title: 'The Smart Planner',
    shortDesc: 'I like good value and plan my spending wisely',
    fullDesc: 'Looks for fair-priced hotels, efficient routes, and experiences that give strong value without overspending.'
  },
  {
    id: 'balance_seeker' as BudgetStyle,
    title: 'The Balance Seeker',
    shortDesc: 'I mix comfort with smart budgeting',
    fullDesc: 'Happy to splurge on a few key experiences but prefers reasonable spending for most of the trip.'
  },
  {
    id: 'premium_picker' as BudgetStyle,
    title: 'The Premium Picker',
    shortDesc: 'I prefer quality, but I choose what\'s truly worth paying for',
    fullDesc: 'Not fully luxury, but values comfort and selectively invests in higher-end stays or activities when it adds real benefit.'
  }
];

export const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'ru', name: 'Russian' },
  { code: 'fr', name: 'French' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' }
];

export const TERMS_AND_CONDITIONS = [
  'HeySriLanka may change hotels and experience providers without compromising your style',
  'HeySriLanka may slightly readjust the itinerary for your best experience',
  'HeySriLanka may share your travel preferences with trusted service partners'
];

export const VEHICLE_TYPES = [
  {
    id: 'car_suv' as VehicleType,
    title: 'Car/SUV',
    capacity: '1-2 people'
  },
  {
    id: 'mini_van' as VehicleType,
    title: 'Mini Van',
    capacity: '3-5 people'
  },
  {
    id: 'large_van' as VehicleType,
    title: 'Large Van',
    capacity: '6-7 people'
  },
  {
    id: 'bus' as VehicleType,
    title: 'Bus',
    capacity: '8-14 people'
  }
];

export const PLACES_TO_VISIT = [
  'Colombo',
  'Galle',
  'Mirissa',
  'Ella',
  'Arugam Bay',
  'Anuradhapura',
  'Sigiriya',
  'Polonnaruwa',
  'Kandy',
  'Nuwara Eliya',
  'Yala',
  'Wilpattu'
];

export const ACTIVITIES_TO_DO = [
  { id: 'food_tour', label: 'Food tour' },
  { id: 'whale_watching', label: 'Whale watching' },
  { id: 'safari', label: 'Safari' },
  { id: 'tea_tasting', label: 'Tea tasting' },
  { id: 'swimming', label: 'Swimming' },
  { id: 'surfing_snorkeling', label: 'Surfing and snorkeling' },
  { id: 'ancient_ruins', label: 'Visit ancient ruins' },
  { id: 'cultural_attractions', label: 'Visit cultural attractions' },
  { id: 'hiking', label: 'Hiking' },
  { id: 'off_beaten_paths', label: 'Visit off the beaten paths' }
];

